import { menuConfig, getPermissionCatalog, getPermissionRouteAliases } from './const';
import { isAdminRole } from '../../consts/management/users/users';
import { getAllowedUserPermissions } from '../../helpers/userPermissions';

const nodeHasPermission = (node, childrenIdsAccess, user) => {
  if (node.permissionBypass) return true;
  if (typeof node.isVisible === 'function' && !node.isVisible(user, childrenIdsAccess)) return false;
  const isLeaf = node.type === 'leaf' || (!node.children && node.path);
  if (isLeaf) {
    if (!childrenIdsAccess) return true;
    return childrenIdsAccess.has(node.permissionId || node.id);
  }
  return node.children?.some((child) => nodeHasPermission(child, childrenIdsAccess, user)) ?? true;
};

export const flattenNavigableLeaves = (nodes, ancestors = []) => nodes.flatMap((node) => {
  const breadcrumbParts = node.label ? [...ancestors, node.label] : ancestors;
  const isLeaf = Boolean(node.path) && (!node.children || node.type === 'leaf' || node.type === 'pinned');
  if (isLeaf) {
    return [{
      ...node,
      breadcrumb: ancestors.join(' › '),
      searchText: breadcrumbParts.join(' › '),
      searchAliases: node.searchAliases || [],
    }];
  }
  return node.children ? flattenNavigableLeaves(node.children, breadcrumbParts) : [];
});

export const filterNavigableLeaves = (leaves, query) => {
  const normalizedQuery = query.trim().toLowerCase();
  if (!normalizedQuery) return leaves;
  const queryTokens = normalizedQuery.split(/\s+/);
  return leaves
    .map((leaf, index) => {
      const aliases = Array.isArray(leaf.searchAliases) ? leaf.searchAliases : [];
      const haystack = `${leaf.label} ${leaf.searchText} ${aliases.join(' ')}`.toLowerCase();
      if (!queryTokens.every((token) => haystack.includes(token))) return null;
      const labelTokens = leaf.label.toLowerCase().split(/\s+/);
      const hasExactAlias = aliases.some((alias) => alias.trim().toLowerCase() === normalizedQuery);
      const hasLabelWordPrefix = queryTokens.every((token) =>
        labelTokens.some((labelToken) => labelToken.startsWith(token))
      );
      return {
        leaf,
        index,
        score: hasExactAlias ? 2 : hasLabelWordPrefix ? 1 : 0,
      };
    })
    .filter(Boolean)
    .sort((left, right) => right.score - left.score || left.index - right.index)
    .map(({ leaf }) => leaf);
};

export const getVisibleMenu = (menu, { childrenIdsAccess, parentIdsAccess, user }) => {
  const visibleNode = (node) => {
    if (node.type === 'pinned') return nodeHasPermission(node, childrenIdsAccess, user) ? node : null;
    if (node.type === 'section' && parentIdsAccess && !parentIdsAccess.has(node.id)) return null;
    if (!nodeHasPermission(node, childrenIdsAccess, user)) return null;
    return { ...node, children: node.children?.map(visibleNode).filter(Boolean) || [] };
  };
  return menu.map(visibleNode).filter(Boolean);
};

export const getUserNavigation = (user) => {
  if (!user) return [];
  const { allowedChildIds, allowedParentIds } = getAllowedUserPermissions({
    permissions: user.permissions,
    isAdmin: isAdminRole(user.role),
    permissionCatalog: getPermissionCatalog(),
    permissionRouteAliases: getPermissionRouteAliases(),
  });
  return flattenNavigableLeaves(getVisibleMenu(menuConfig, {
    childrenIdsAccess: allowedChildIds,
    parentIdsAccess: allowedParentIds,
    user,
  }));
};
